/**
 * SourceControlMobileDrawer Component
 * Mobile-specific bottom sheet for source control toggles.
 * Provides the same functionality as SourceControlPopover but optimized for touch.
 */

import { TierProFilled } from '@heidi/design-system/icons';
import { toggleWebSearchAtom } from '@/atoms/sourceControlAtoms';;
import { isSoleEnabledChatSourceBucket } from '@/atoms/chatSourceRequireOne';
import { PartnerLogo } from '@heidi/evidence';
import {
  addingMoreJournalsDailyMessage,
  fullTextSearchComingSoonMessage,
  hasWebSearchJournals,
} from '@/components/chat/constants/integrationPartners';
import { SourceControlPopoverV2Banners } from '@/components/chat/components/source-control-v2/components/source-control-popover-v2-banners';
import { useSourceControlActions } from '@/components/chat/components/source-control-v2/hooks/useSourceControlActions';
import { useAvailableIntegrations } from '@/components/chat/hooks/useAvailableIntegrations';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { usePrimarySourcesDefaultInit } from '@/components/chat/hooks/usePrimarySourcesDefaultInit';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@heidi/design-system/drawer';
import { Switch } from '@heidi/design-system/switch';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { LastSourceRequiredTooltip } from '@/components/chat/components/source-control-v2/LastSourceRequiredTooltip';
import { useCollections } from '@heidi/evidence/data';
import { isIdInAllowlist } from '@heidi/evidence/contracts';
import { useSourceLists } from '@heidi/evidence/data';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useTeamEvidenceEnforcement } from '@/hooks/useTeamEvidenceEnforcement';
import { cn } from '@/lib/cn';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { BadgeCheck, BookOpen, ChevronDown, Folder, FolderOpen, Globe, LibraryBig, Lock, Stethoscope, TextSearch } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

interface SourceControlMobileDrawerProps {
  /** Optional children to use as custom trigger */
  children?: ReactNode;
  /** Optional controlled open state */
  open?: boolean;
  /** Optional callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

export const SourceControlMobileDrawer = ({
  children,
  open: controlledOpen,
  onOpenChange,
}: SourceControlMobileDrawerProps) => {
  return (
    <MobileDrawerV2
      controlledOpen={controlledOpen}
      onOpenChange={onOpenChange}
    >
      {children}
    </MobileDrawerV2>
  );
};

const MobileDrawerV2 = ({
  children,
  controlledOpen,
  onOpenChange,
}: {
  children?: ReactNode;
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const { t } = useTranslations();
  const intl = useIntl();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const { baseProps } = useEvidenceAnalytics();

  const {
    toggleState,
    handleDocCollectionToggle,
    handleWebCollectionToggle,
    handleUncollectedToggle,
    handlePartnerToggle,
    handleClinicalSourcesToggle,
    resetUncollected,
    isRequireAtLeastOneSource,
  } = useSourceControlActions(baseProps);
  const toggleWebSearch = useSetAtom(toggleWebSearchAtom);
  const { hasEvidencePro } = useEvidenceBillingInfo();

  const {
    enforcedLibraryCollectionIds,
    libraryCollectionAllowlistIds,
    libraryCollectionIdsChangeable,
    enforcedWebCollectionIds,
    webCollectionIdsChangeable,
    isPerCollectionLibraryModesEnabled,
  } = useTeamEvidenceEnforcement();

  const hasLibraryEnforcement = libraryCollectionAllowlistIds != null;
  const hasWebEnforcement = enforcedWebCollectionIds != null;
  const showUncollectedToggle =
    libraryCollectionIdsChangeable && !hasLibraryEnforcement;

  useEffect(() => {
    if (hasLibraryEnforcement || !libraryCollectionIdsChangeable) {
      resetUncollected();
    }
  }, [hasLibraryEnforcement, libraryCollectionIdsChangeable, resetUncollected]);

  const { data: collectionsData } = useCollections();
  const allCollections = collectionsData?.collections ?? [];
  const { data: sourceLists } = useSourceLists();
  const allWebCollections = sourceLists ?? [];
  const { partners: availablePartners } = useAvailableIntegrations();

  const collections = useMemo(
    () =>
      hasLibraryEnforcement
        ? allCollections.filter((c) =>
            isIdInAllowlist(
              c.collection_id,
              libraryCollectionAllowlistIds,
              c.legacy_source_list_id
            )
          )
        : allCollections,
    [allCollections, libraryCollectionAllowlistIds, hasLibraryEnforcement]
  );

  const enforcedLibraryIdSet = useMemo(
    () => new Set(enforcedLibraryCollectionIds ?? []),
    [enforcedLibraryCollectionIds]
  );

  const isLibraryCollectionLocked = (collectionId: string) => {
    if (isPerCollectionLibraryModesEnabled) {
      return enforcedLibraryIdSet.has(collectionId);
    }
    return !libraryCollectionIdsChangeable;
  };

  const webCollections = useMemo(
    () =>
      hasWebEnforcement
        ? allWebCollections.filter((c) =>
            isIdInAllowlist(c.id, enforcedWebCollectionIds)
          )
        : allWebCollections,
    [allWebCollections, enforcedWebCollectionIds, hasWebEnforcement]
  );

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isClinicalToolsOpen, setIsClinicalToolsOpen] = useState(false);

  usePrimarySourcesDefaultInit();

  const isClinicalSourcesOn = toggleState.localGuidelines || toggleState.primaryEvidence;
  const lockLastWebSearch =
    isRequireAtLeastOneSource &&
    isSoleEnabledChatSourceBucket(toggleState, 'webSearch');
  const lockLastClinicalSources =
    isRequireAtLeastOneSource &&
    isSoleEnabledChatSourceBucket(toggleState, 'clinicalSources');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setInternalOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  const defaultTrigger = (
    <Button type="button" variant="ghost" size="lg" data-testid="sources-button" iconOnly>
      <TextSearch />
    </Button>
  );

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>{children ?? defaultTrigger}</DrawerTrigger>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="border-b border-border pb-3">
          <DrawerTitle className="text-center">
            {t('sources.sourcesButton' as TranslationKey)}
          </DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col gap-1 overflow-y-auto p-3">
          {/* 1. Web search toggle — pro only */}
          <div
            className={cn(
              'flex items-start justify-between gap-3 rounded-lg p-3 transition-colors',
              toggleState.webSearch && 'bg-surface'
            )}
          >
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <Stethoscope size={18} className="text-text-primary" />
                <TypographyP2 className="font-normal text-text-primary">
                  {t('sources.sourceControl.webSearch' as TranslationKey)}
                </TypographyP2>
              </div>
              <div className="pl-[26px]">
                <TypographyCaption className="text-text-tertiary">
                  {t('sources.sourceControl.webSearchDescription' as TranslationKey)}
                </TypographyCaption>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {!hasEvidencePro && <TierProFilled className="size-3 text-tier-pro" />}
              {lockLastWebSearch && hasEvidencePro ? (
                <LastSourceRequiredTooltip>
                  <Switch
                    checked={toggleState.webSearch}
                    onCheckedChange={toggleWebSearch}
                    disabled={!hasEvidencePro || lockLastWebSearch}
                    size="sm"
                    aria-label="Toggle clinical web"
                  />
                </LastSourceRequiredTooltip>
              ) : (
                <Switch
                  checked={toggleState.webSearch}
                  onCheckedChange={toggleWebSearch}
                  disabled={!hasEvidencePro || lockLastWebSearch}
                  size="sm"
                  aria-label="Toggle clinical web"
                />
              )}
            </div>
          </div>

          {/* 2. Clinical sources toggle — pro only */}
          <div
            className={cn(
              'flex items-start justify-between gap-3 rounded-lg p-3 transition-colors',
              isClinicalSourcesOn && 'bg-surface'
            )}
          >
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <BookOpen size={18} className="text-text-primary" />
                <TypographyP2 className="font-normal text-text-primary">
                  {t('sources.sourceControl.clinicalSources' as TranslationKey)}
                </TypographyP2>
              </div>
              <div className="flex flex-col gap-1 pl-[26px]">
                <TypographyCaption className="text-text-tertiary">
                  {t('sources.sourceControl.clinicalSourcesDescription' as TranslationKey)}
                </TypographyCaption>
                <TypographyCaption className="text-text-tertiary">
                  {intl.formatMessage({
                    defaultMessage: 'Searches may take longer when this is on.',
                    id: 'U3meJmBV1o',
                  })}
                </TypographyCaption>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {!hasEvidencePro && <TierProFilled className="size-3 text-tier-pro" />}
              {lockLastClinicalSources && hasEvidencePro ? (
                <LastSourceRequiredTooltip>
                  <Switch
                    checked={isClinicalSourcesOn}
                    onCheckedChange={handleClinicalSourcesToggle}
                    disabled={!hasEvidencePro || lockLastClinicalSources}
                    size="sm"
                    aria-label="Toggle literature and guidelines"
                  />
                </LastSourceRequiredTooltip>
              ) : (
                <Switch
                  checked={isClinicalSourcesOn}
                  onCheckedChange={handleClinicalSourcesToggle}
                  disabled={!hasEvidencePro || lockLastClinicalSources}
                  size="sm"
                  aria-label="Toggle literature and guidelines"
                />
              )}
            </div>
          </div>

          {/* 3. My library — pro only, collapsible on mobile */}
          <Collapsible open={isLibraryOpen} onOpenChange={setIsLibraryOpen}>
            <CollapsibleTrigger asChild disabled={!hasEvidencePro}>
              <button
                type="button"
                disabled={!hasEvidencePro}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-lg py-3 pl-3 pr-5 text-left transition-colors',
                  hasEvidencePro && 'cursor-pointer hover:bg-surface-hover',
                  !hasEvidencePro && 'cursor-not-allowed'
                )}
                aria-label="My library"
              >
                <div className="flex items-center gap-2">
                  <LibraryBig
                    size={18}
                    className={!hasEvidencePro ? 'text-text-disabled' : 'text-text-primary'}
                  />
                  <TypographyP2
                    className={cn(
                      'font-normal',
                      !hasEvidencePro ? 'text-text-disabled' : 'text-text-primary'
                    )}
                  >
                    {t('sources.sourceControl.myLibrary' as TranslationKey)}
                  </TypographyP2>
                </div>
                <div className="flex items-center gap-1">
                  {!hasEvidencePro && (
                    <TierProFilled className="size-3 text-tier-pro" />
                  )}
                  <ChevronDown
                    size={16}
                    className={cn(
                      'transition-transform duration-200 ease-out',
                      !isLibraryOpen && 'rotate-180',
                      !hasEvidencePro ? 'text-text-disabled' : 'text-text-secondary'
                    )}
                  />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
              <div className="flex flex-col gap-0.5 pb-2 pl-6 pr-3">
                {/* Hidden when library selection is locked (EVI-1956) */}
                {showUncollectedToggle && (
                  <button
                    type="button"
                    onClick={() => handleUncollectedToggle()}
                    className="flex w-full items-center justify-between gap-2 rounded-md p-2 text-left hover:bg-surface-hover"
                  >
                    <div className="flex items-center gap-2">
                      {toggleState.includeUncollected ? (
                        <FolderOpen size={16} className="text-text-secondary" />
                      ) : (
                        <Folder size={16} className="text-text-secondary" />
                      )}
                      <div className="flex flex-col">
                        <TypographyP2 className={cn('font-normal', 'text-text-primary')}>
                          {t('sources.sourceControl.uncollectedDocuments' as TranslationKey)}
                        </TypographyP2>
                        <TypographyCaption className="text-text-tertiary">
                          {t(
                            'sources.sourceControl.uncollectedDocumentsDescription' as TranslationKey
                          )}
                        </TypographyCaption>
                      </div>
                    </div>
                    <Switch
                      size="xs"
                      checked={toggleState.includeUncollected}
                      onCheckedChange={() => handleUncollectedToggle()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </button>
                )}

                {/* Document collections */}
                {collections.map((collection) => {
                  const isSelected = toggleState.selectedCollectionIds.includes(
                    collection.collection_id
                  );
                  const isLocked = isLibraryCollectionLocked(collection.collection_id);
                  return (
                    <button
                      type="button"
                      key={collection.collection_id}
                      onClick={
                        isLocked
                          ? undefined
                          : () => handleDocCollectionToggle(collection.collection_id)
                      }
                      disabled={isLocked}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-md p-2 text-left hover:bg-surface-hover',
                        isLocked && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <TypographyP2 className="truncate font-normal text-text-primary">
                          {collection.name}
                        </TypographyP2>
                        <TypographyCaption className="text-text-secondary">
                          {t(
                            `sources.sourceControl.documents_${collection.document_count === 1 ? 'one' : 'other'}` as TranslationKey,
                            { count: collection.document_count }
                          )}
                        </TypographyCaption>
                      </div>
                      {isLocked ? (
                        <Lock size={14} className="text-text-tertiary" />
                      ) : (
                        <Checkbox checked={isSelected} />
                      )}
                    </button>
                  );
                })}

                {/* Web source lists */}
                {webCollections.map((collection) => {
                  const isSelected = toggleState.selectedWebCollectionIds.includes(collection.id);
                  const isLocked = !webCollectionIdsChangeable;
                  return (
                    <button
                      type="button"
                      key={collection.id}
                      onClick={
                        isLocked ? undefined : () => handleWebCollectionToggle(collection.id)
                      }
                      disabled={isLocked}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-md p-2 text-left hover:bg-surface-hover',
                        isLocked && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <TypographyP2 className="truncate font-normal text-text-primary">
                          {collection.name}
                        </TypographyP2>
                        <TypographyCaption className="text-text-secondary">
                          {t(
                            `sources.sourceControl.domain_${collection.domains.length === 1 ? 'one' : 'other'}` as TranslationKey,
                            { count: collection.domains.length }
                          )}
                        </TypographyCaption>
                      </div>
                      {isLocked ? (
                        <Lock size={14} className="text-text-tertiary" />
                      ) : (
                        <Checkbox checked={isSelected} />
                      )}
                    </button>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 4. Clinical tools — pro only, collapsible on mobile */}
          {availablePartners.length > 0 && (
            <Collapsible open={isClinicalToolsOpen} onOpenChange={setIsClinicalToolsOpen}>
              <CollapsibleTrigger asChild disabled={!hasEvidencePro}>
                <button
                  type="button"
                  disabled={!hasEvidencePro}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-lg py-3 pl-3 pr-5 text-left transition-colors',
                    hasEvidencePro && 'cursor-pointer hover:bg-surface-hover',
                    !hasEvidencePro && 'cursor-not-allowed'
                  )}
                  aria-label={t('sources.sourceControl.clinicalTools' as TranslationKey)}
                >
                  <div className="flex items-center gap-2">
                    <BadgeCheck size={18} className={!hasEvidencePro ? 'text-text-disabled' : 'text-text-primary'} />
                    <TypographyP2
                      className={cn(
                        'font-normal',
                        !hasEvidencePro ? 'text-text-disabled' : 'text-text-primary'
                      )}
                    >
                      {t('sources.sourceControl.clinicalTools' as TranslationKey)}
                    </TypographyP2>
                  </div>
                  <div className="flex items-center gap-1">
                    {!hasEvidencePro && (
                      <TierProFilled className="size-3 text-tier-pro" />
                    )}
                    <ChevronDown
                      size={16}
                      className={cn(
                        'transition-transform duration-200 ease-out',
                        !isClinicalToolsOpen && 'rotate-180',
                        !hasEvidencePro ? 'text-text-disabled' : 'text-text-secondary'
                      )}
                    />
                  </div>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                <div className="flex flex-col gap-0.5 pb-2 pl-6 pr-3">
                  {availablePartners.map((partner) => {
                    const isEnabled = toggleState.integrationPartners[partner.id] ?? false;
                    return (
                      <button
                        type="button"
                        key={partner.id}
                        onClick={() => handlePartnerToggle(partner.id)}
                        className="flex w-full items-center justify-between gap-2 rounded-md p-2 text-left hover:bg-surface-hover"
                      >
                        <div className="flex items-center gap-2">
                          <PartnerLogo
                            src={partner.logo}
                            brandColor={partner.brandColor}
                            size={18}
                            className="shrink-0"
                            alt={partner.displayName}
                          />
                          {partner.useTavily ? (
                            <div className="flex flex-col">
                              <span className="inline-flex items-center gap-1">
                                <TypographyP2 className="font-normal text-text-primary">
                                  {partner.displayName}
                                </TypographyP2>
                                <Globe
                                  className="size-3.5 shrink-0 text-text-tertiary"
                                  aria-hidden
                                  data-testid={`partner-web-search-${partner.id}`}
                                />
                              </span>
                              <TypographyCaption className="text-text-tertiary">
                                {intl.formatMessage(fullTextSearchComingSoonMessage)}
                              </TypographyCaption>
                            </div>
                          ) : (
                            <TypographyP2 className="font-normal text-text-primary">
                              {partner.displayName}
                            </TypographyP2>
                          )}
                        </div>
                        <Checkbox
                          checked={isEnabled}
                          onCheckedChange={() => handlePartnerToggle(partner.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </button>
                    );
                  })}
                  {hasWebSearchJournals(availablePartners) && (
                    <TypographyCaption className="px-2 py-1 text-text-tertiary">
                      {intl.formatMessage(addingMoreJournalsDailyMessage)}
                    </TypographyCaption>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Trial/Pro Banners */}
        <SourceControlPopoverV2Banners
          variant="drawer"
          onClose={() => handleOpenChange(false)}
        />
      </DrawerContent>
    </Drawer>
  );
};
